import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Card, Button, Container, Form, Spinner, Alert } from 'react-bootstrap';
import { toast } from 'react-toastify';
import npsService from '../services/npsService';

const NPSThankYouPage = () => {
  const { token, score } = useParams();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [comment, setComment] = useState('');
  const [commentSubmitted, setCommentSubmitted] = useState(false);
  const [npsData, setNpsData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Record the score when the component mounts
    const recordScore = async () => {
      try {
        const response = await npsService.recordScore(token, score);
        setNpsData(response.data);
        setLoading(false);
      } catch (err) {
        console.error('Error recording NPS score:', err);
        setError(err.response?.data?.message || 'Une erreur est survenue');
        setLoading(false);
      }
    };

    if (token && score) {
      recordScore();
    }
  }, [token, score]);

  const handleCommentSubmit = async (e) => {
    e.preventDefault();

    if (!comment.trim()) {
      toast.warning('Entre un commentaire');
      return;
    }

    setSubmitting(true);
    try {
      await npsService.submitComment(token, comment);
      setCommentSubmitted(true);
      toast.success('Merci pour ton commentaire !');
    } catch (err) {
      console.error('Error submitting comment:', err);
      toast.error('Erreur lors de l\'envoi du commentaire');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Container className="d-flex align-items-center justify-content-center min-vh-100">
        <Spinner animation="border" variant="primary" />
      </Container>
    );
  }

  if (error) {
    return (
      <Container className="d-flex align-items-center justify-content-center min-vh-100">
        <Card className="text-center shadow" style={{ maxWidth: '600px', width: '100%' }}>
          <Card.Body className="p-5">
            <div className="mb-4">
              <div className="text-warning mb-3" style={{ fontSize: '80px' }}>
                !
              </div>
              <h2 className="mb-3">Oups !</h2>
              <Alert variant="warning">
                {error}
              </Alert>
            </div>
          </Card.Body>
        </Card>
      </Container>
    );
  }

  return (
    <Container className="d-flex align-items-center justify-content-center min-vh-100">
      <Card className="text-center shadow" style={{ maxWidth: '600px', width: '100%' }}>
        <Card.Body className="p-5">
          {/* Logo */}
          <div className="mb-4">
            <img
              src="https://img.mailinblue.com/7320067/images/content_library/original/662637fb2a1bc1605466bed0.png"
              alt="Marion"
              style={{ maxWidth: '150px' }}
            />
          </div>

          {/* Success Icon */}
          <div className="mb-4">
            <div
              className="d-inline-flex align-items-center justify-content-center text-white mb-3 animate-scale-in"
              style={{
                width: '80px',
                height: '80px',
                borderRadius: '50%',
                backgroundColor: '#28a745',
                fontSize: '50px'
              }}
            >
              ✓
            </div>
          </div>

          {/* Thank You Message */}
          <h2 className="mb-3 text-success">Merci pour ton retour !</h2>

          {score && (
            <div
              className="d-inline-block mb-4 px-4 py-2"
              style={{
                backgroundColor: '#013f78',
                color: 'white',
                borderRadius: '25px',
                fontSize: '24px',
                fontWeight: 'bold'
              }}
            >
              {score}/10
            </div>
          )}

          <p className="text-muted mb-4">
            Ton avis a bien été enregistré et nous aide à améliorer nos services.
          </p>

          {/* Optional Comment Form */}
          {!commentSubmitted && !npsData?.hasComment && (
            <div className="mt-4 pt-4" style={{ borderTop: '2px solid #f0f0f0' }}>
              <h5 className="mb-3">Souhaites-tu ajouter un commentaire ?</h5>
              <p className="text-muted small">(optionnel)</p>

              <Form onSubmit={handleCommentSubmit}>
                <Form.Group className="mb-3">
                  <Form.Control
                    as="textarea"
                    rows={4}
                    placeholder="Partage ton expérience avec nous... (facultatif)"
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                    maxLength={2000}
                    disabled={submitting}
                  />
                  <Form.Text className="text-muted">
                    {comment.length}/2000 caractères
                  </Form.Text>
                </Form.Group>

                <Button
                  type="submit"
                  variant="success"
                  size="lg"
                  disabled={submitting}
                  className="w-100"
                >
                  {submitting ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-2" />
                      Envoi en cours...
                    </>
                  ) : (
                    'Envoyer mon commentaire'
                  )}
                </Button>
              </Form>
            </div>
          )}

          {/* Comment Submitted */}
          {commentSubmitted && (
            <Alert variant="success" className="mt-4">
              <strong>Ton commentaire a également été enregistré. Merci beaucoup !</strong>
            </Alert>
          )}

          {/* Footer */}
          <div className="mt-5 pt-4" style={{ borderTop: '1px solid #e0e0e0', fontSize: '14px', color: '#999' }}>
            <p className="mb-1"><strong>Marion</strong></p>
            <p className="mb-0">559 CHE DU HAMEAU DE PLAN SARRAIN, 06370, MOUANS-SARTOUX</p>
          </div>
        </Card.Body>
      </Card>

      <style>{`
        @keyframes scaleIn {
          from {
            transform: scale(0);
            opacity: 0;
          }
          to {
            transform: scale(1);
            opacity: 1;
          }
        }
        .animate-scale-in {
          animation: scaleIn 0.5s ease-out;
        }
      `}</style>
    </Container>
  );
};

export default NPSThankYouPage;
